import React, { useState, useEffect, useMemo, useRef } from "react";
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { logEvento, TIPI_EVENTO } from "../utils/logEvento";
import { confirm } from "../utils/confirm";
import SquadraEditor, { squadraTotale } from "../components/SquadraEditor";
import EsecuzioneCommessaEditor from "../components/EsecuzioneCommessaEditor";
import { etichettaSquadra, impreseDaSalvare, avvisiEsecuzione } from "../utils/esecuzioneCommessa";
import { SelettoreSede } from "../components/SediCliente";
import { trovaSedePerIndirizzo, copiaSede, copiaLibera } from "../utils/sediCliente";
import { giorniLavorativi } from "../utils/esecuzioneCommessa";
import { attivaSicurezzaCantiere } from "../utils/sicurezzaCantiereDb";
import { GIORNI_SUGGERIMENTO_CANTIERE } from "../utils/sicurezzaCantiere";
import { usePiano } from "../utils/usePiano";
import { statoModulo } from "../utils/pianiNexiora";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { bloccaSeReadOnly } from "../utils/trialGate";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { X, Building2, Plus } from "lucide-react";
import { parseValutaItaliana } from "../utils/clienteHelpers";
import { pulisciNumeroOrdine } from "../utils/numeroOrdine";

const COLORS = ["#7c3aed","#2563eb","#059669","#dc2626","#d97706","#0891b2","#db2777"];

// ── Modal selezione cliente (crea progetto padre) ────────────────────────────
function ModalSelezionaCliente({ onClose, onCreated, onNavigate, prefill }) {
  const { currentUser, isReadOnly } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [clienti, setClienti] = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [loading, setLoading] = useState(false);
  const [parentPronto, setParentPronto] = useState(null); // {id, clienteNome} — quando pronto, si passa al form commessa vera

  useEffect(()=>{
    if(!effectiveOrgId) return;
    getDocs(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)))
      .then(snap=>{
        const list = snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.nome||a.ragioneSociale||"").localeCompare(b.nome||b.ragioneSociale||"","it"));
        setClienti(list);
      });
  },[effectiveOrgId]);

  const crea = async () => {
    if(!clienteId){ addToast("Seleziona un cliente","error"); return; }
    if (bloccaSeReadOnly(isReadOnly, addToast)) return;
    setLoading(true);
    try {
      const cliente = clienti.find(c=>c.id===clienteId);
      const nomeCliente = cliente?.nome || cliente?.ragioneSociale || "Cliente";
      // Il cliente ha già la sua cartella commesse? Se sì la riuso, non blocco più con un errore.
      const snap = await getDocs(query(collection(db,"projects"),
        where("organizationId","==",effectiveOrgId),
        where("clienteId","==",clienteId),
        where("isParent","==",true)));
      if(!snap.empty){
        const esistente = snap.docs[0];
        setParentPronto({ id: esistente.id, clienteNome: esistente.data().clienteNome || nomeCliente });
        setLoading(false);
        return;
      }
      const ref = await addDoc(collection(db,"projects"),{
        name: nomeCliente,
        clienteId, clienteNome: nomeCliente,
        isParent: true,
        color: COLORS[Math.floor(Math.random()*COLORS.length)],
        organizationId: effectiveOrgId,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        archived: false,
      });
      setParentPronto({ id: ref.id, clienteNome: nomeCliente });
    } catch(err){ addToast(messaggioErrore(err, "creare il cliente"),"error"); }
    setLoading(false);
  };

  if (parentPronto) {
    return <ModalNuovaCommessa parentId={parentPronto.id} clienteNome={parentPronto.clienteNome} prefill={prefill} onClose={onClose} onCreated={onCreated} onNavigate={onNavigate}/>;
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:420}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:8}}><Building2 size={15} color="#7c3aed"/>Seleziona cliente</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <p style={{fontSize:12,color:"#6b7280",marginBottom:16}}>Seleziona il cliente per cui creare la commessa. Per aggiungere un nuovo cliente vai nella sezione Clienti.</p>
        <div className="form-group">
          <label className="form-label">Cliente *</label>
          <select className="input" value={clienteId} onChange={e=>setClienteId(e.target.value)}>
            <option value="">— Seleziona cliente —</option>
            {clienti.map(c=><option key={c.id} value={c.id}>{c.nome||c.ragioneSociale}</option>)}
          </select>
        </div>
        {clienti.length===0 && <div style={{fontSize:12,color:"#f59e0b",marginBottom:12}}>⚠️ Nessun cliente in anagrafica. Vai nella sezione Clienti per aggiungerne uno.</div>}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={crea} disabled={loading||!clienteId}>
            {loading?"Un attimo...":"Continua"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal nuova commessa (dentro un cliente) ─────────────────────────────────
function ModalNuovaCommessa({ parentId, clienteNome, prefill, senzaCliente, onClose, onCreated, onNavigate }) {
  const { currentUser, isPlatformOwner, isOrgAdmin, isReadOnly } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const isAdmin = isPlatformOwner || isOrgAdmin;
  const [responsabileUid, setResponsabileUid] = useState("");
  const [squadra, setSquadra] = useState(()=>{
    if(prefill?.squadra&&prefill.squadra.length>0) return prefill.squadra;
    if(prefill?.operaiNecessari) return Array.from({length:parseInt(prefill.operaiNecessari)||1},()=>({livello:parseInt(prefill?.livelloMin)||0}));
    return [{livello:0}]; // default: 1 operaio non specificato
  });
  const [form, setForm] = useState({ name:prefill?.nome||"", codiceCommessa:prefill?.codiceCommessa||"", numeroOrdine:"", color:COLORS[0], description:"", dataInizio:prefill?.dataInizio||"", dataFine:"", oreGiornaliere:"8", manutenzioneCommessa:prefill?.manutenzioneCommessa||false, manutenzioneDiId:"" });

  // Sede dei lavori (anagrafica cliente → Sedi e siti, utils/sediCliente.js)
  const [clienteIdSede, setClienteIdSede] = useState("");
  const [sedeLavori, setSedeLavori] = useState(null);
  const [propostaSede, setPropostaSede] = useState(null);

  // Chi esegue i lavori (utils/esecuzioneCommessa.js): interno / subappalto / misto
  const [tipoEsecuzione, setTipoEsecuzione] = useState(prefill?.tipoEsecuzione || "interno");
  const [impreseEsterne, setImpreseEsterne] = useState(prefill?.impreseEsterne || []);
  const cambiaTipoEsecuzione = (t) => {
    // Subappalto: la forza lavoro di produzione si azzera, resta 1 persona di presidio
    // (modificabile). La commessa resta in pianificazione con il carico reale del presidio.
    if (t === "subappalto" && tipoEsecuzione !== "subappalto" && squadra.length > 1) {
      setSquadra([{livello:0}]);
      addToast("Squadra di produzione azzerata: resta 1 persona di presidio interno (puoi cambiarla)","info");
    }
    setTipoEsecuzione(t);
  };
  const etSquadra = etichettaSquadra(tipoEsecuzione);

  // Sicurezza di cantiere (piano Pro): "È un cantiere?" e responsabile della sicurezza
  const { stato: statoPianoOrg } = usePiano(effectiveOrgId);
  // Decisione di Paolo (ott 2026): ogni commessa che è un cantiere nasce con la sicurezza
  // attivata, chiunque la crei — la domanda la vedono tutti (piano Pro).
  const sicurezzaDisponibile = statoModulo("sicurezza", statoPianoOrg) === "full";
  const [eCantiere, setECantiere] = useState(null); // null = non ancora risposto
  const [respSicurezza, setRespSicurezza] = useState("");

  const [loading, setLoading] = useState(false);
  const [utenti, setUtenti] = useState([]);
  const [membri, setMembri] = useState([]); // [{uid, nome, ruolo}]
  const [commesseCliente, setCommesseCliente] = useState([]);
  const [tutteCommesseOrg, setTutteCommesseOrg] = useState([]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    getDocs(query(collection(db,"projects"),
      where("organizationId","==",effectiveOrgId), where("isParent","==",false))).then(snap=>{
      setTutteCommesseOrg(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived));
    });
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId || !parentId) return;
    getDoc(doc(db,"projects",parentId)).then(async parentSnap=>{
      const clienteId = parentSnap.exists() ? parentSnap.data().clienteId : null;
      if(!clienteId) return;
      setClienteIdSede(clienteId);
      // Sede dei lavori proposta: quella dell'offerta (se c'è) riconosciuta tra le sedi del cliente
      if (prefill?.sitoCliente) {
        const cs = await getDoc(doc(db,"clienti",clienteId));
        const trovata = cs.exists() ? trovaSedePerIndirizzo(cs.data(), prefill.sitoCliente) : null;
        setPropostaSede(prefill?.sede?.sedeId ? prefill.sede : (trovata ? copiaSede(trovata) : copiaLibera(prefill.sitoCliente)));
      } else if (prefill?.sede) setPropostaSede(prefill.sede);
      const snap = await getDocs(query(collection(db,"projects"),
        where("organizationId","==",effectiveOrgId), where("clienteId","==",clienteId), where("isParent","==",false)));
      setCommesseCliente(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived));
    });
  },[effectiveOrgId, parentId]);

  // ── Numerazione automatica commesse (facoltativa, in base al formato scelto in Impostazioni) ──
  const [orgSettings, setOrgSettings] = useState({});
  useEffect(()=>{
    if(!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(s=>{ if(s.exists()) setOrgSettings(s.data()); });
  },[effectiveOrgId]);

  const codiceSuggerito = useMemo(() => {
    if (!orgSettings.formatoNumerazioneCommesse) return "";
    const codiciValidi = tutteCommesseOrg.map(c=>c.codiceCommessa).filter(c=>c && /^\d+-\d+$/.test(c));
    if (codiciValidi.length===0) return ""; // prima commessa dell'organizzazione: nessun dato da cui suggerire
    const annoCorrente = String(new Date().getFullYear()).slice(-2);
    const annoProgressivo = orgSettings.formatoNumerazioneCommesse === "anno-progressivo";
    let maxProgressivo = 0;
    codiciValidi.forEach(c=>{
      const [a,b] = c.split("-");
      const anno = annoProgressivo ? a : b;
      const progressivo = annoProgressivo ? b : a;
      if (anno === annoCorrente) {
        const n = parseInt(progressivo,10);
        if (!isNaN(n) && n > maxProgressivo) maxProgressivo = n;
      }
    });
    const prossimo = maxProgressivo + 1; // se 0 (nessuna commessa di quest'anno): riparte da 1, azzeramento automatico al cambio anno
    return annoProgressivo ? `${annoCorrente}-${prossimo}` : `${prossimo}-${annoCorrente}`;
  }, [tutteCommesseOrg, orgSettings, senzaCliente]);

  useEffect(()=>{
    if (codiceSuggerito && !form.codiceCommessa) {
      setForm(f=>({...f, codiceCommessa: codiceSuggerito}));
    }
  },[codiceSuggerito]);


  useEffect(()=>{
    if(!effectiveOrgId) return;
    getDocs(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)))
      .then(snap=>{
        const list = snap.docs.map(d=>d.data()).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it"));
        setUtenti(list);
        // Nessuna auto-assegnazione — il responsabile deve essere scelto esplicitamente
      })
      .catch(err=>{
        console.error("Errore caricamento utenti per Responsabile commessa:", err);
        addToast("Impossibile caricare l'elenco utenti: "+(err?.message||"errore sconosciuto"),"error");
      });
  },[effectiveOrgId]);

  const toggleMembro = (utente) => {
    const esiste = membri.find(m=>m.uid===utente.uid);
    if(esiste){
      if(esiste.ruolo==="responsabile" && membri.filter(m=>m.ruolo==="responsabile").length===1){
        return; // almeno un responsabile sempre
      }
      setMembri(m=>m.filter(x=>x.uid!==utente.uid));
    } else {
      setMembri(m=>[...m, {uid:utente.uid, nome:utente.displayName, ruolo:"operativo"}]);
    }
  };
  const cambiaRuolo = (uid, ruolo) => setMembri(m=>m.map(x=>x.uid===uid?{...x,ruolo}:x));

  // Blocco anti doppio invio, attivo dal PRIMO istante del clic: prima il pulsante
  // restava cliccabile durante il controllo doppioni e la finestra di conferma (entrambi
  // asincroni), quindi un secondo clic ravvicinato faceva partire una seconda creazione
  // completa, con lo stesso codice, prima che la prima avesse scritto nulla.
  const invioInCorso = useRef(false);
  const submit = async e => {
    e?.preventDefault?.();
    if (invioInCorso.current) return;
    invioInCorso.current = true;
    setLoading(true);
    try { await eseguiSubmit(); }
    finally { invioInCorso.current = false; setLoading(false); }
  };

  const eseguiSubmit = async () => {
    if (bloccaSeReadOnly(isReadOnly, addToast)) return;
    if(!form.name.trim()){ addToast("Nome commessa obbligatorio","error"); return; }
    if(!form.codiceCommessa.trim()){ addToast("Codice commessa obbligatorio (es. 56-26)","error"); return; }
    if(!responsabileUid && utenti.length>0){ addToast("Seleziona il Responsabile della commessa — campo obbligatorio","error"); return; }
    // Valida formato codice: numeri-numeri (es. 56-26, 1000-26)
    const codiceRegex = /^\d+-\d+$/;
    if(!codiceRegex.test(form.codiceCommessa.trim())){
      addToast("Formato codice non valido. Usa il formato: numero-anno (es. 56-26, 1000-26)","error");
      return;
    }

    // Controlla duplicati: il codiceCommessa deve essere univoco nell'organizzazione
    const allSnap = await getDocs(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)));
    const dupCodice = allSnap.docs.find(d=>
      (d.data().codiceCommessa||"").toLowerCase() === form.codiceCommessa.trim().toLowerCase()
      && !d.data().archived && !d.data().isParent
    );
    if(dupCodice){ addToast(`Esiste già una commessa con codice "${form.codiceCommessa.trim()}" — scegli un codice diverso`, "error"); return; }

    if (sicurezzaDisponibile && !senzaCliente && eCantiere===null) { addToast("Indica se la commessa è un cantiere (sicurezza di cantiere)","error"); return; }
    if (sicurezzaDisponibile && eCantiere===true && !respSicurezza && utenti.length>0) { addToast("Scegli l'addetto alla sicurezza e alla documentazione di cantiere","error"); return; }

    // Subappalto/misto senza imprese indicate: avviso non bloccante
    const avvisiEs = avvisiEsecuzione(tipoEsecuzione, impreseEsterne);
    if (avvisiEs.length>0) {
      const ok = await confirm(avvisiEs.join("\n")+"\n\nVuoi creare la commessa lo stesso?", { title:"Imprese esterne", type:"warning", confirmLabel:"Sì, crea commessa" });
      if(!ok){ setLoading(false); return; }
    }

    // Avviso se piano di carico incompleto — non bloccante
    const pianoCompleto = form.dataInizio && form.dataFine && squadra.length>0 && squadraTotale(squadra)>0;
    if(!pianoCompleto){
      const ok = await confirm(
        "Stai aprendo una commessa senza il piano di carico completo (data inizio/fine e operai necessari).\n\nLa commessa verrà creata ma il Simulatore Disponibilità Maestranze non potrà calcolare l'impatto sul carico di lavoro.\n\nVuoi continuare comunque?",
        { title:"Piano di carico incompleto", type:"warning", confirmLabel:"Sì, crea commessa" }
      );
      if(!ok){ setLoading(false); return; }
    }

    setLoading(true);
    try {
      // Leggi clienteId dal progetto padre — solo se questa commessa è associata a un cliente
      let clienteId = "", cNome = "";
      if (!senzaCliente) {
        const parentSnap = await getDoc(doc(db,"projects",parentId));
        clienteId = parentSnap.data()?.clienteId || "";
        cNome = parentSnap.data()?.clienteNome || clienteNome;
      }

      const data = {
        name: form.name.trim(),
        codiceCommessa: form.codiceCommessa.trim(),
        numeroOrdine: pulisciNumeroOrdine(form.numeroOrdine),
        // Sempre valorizzato alla creazione: le query filtrano per statoCommessa lato server
        // (per non scaricare l'intero storico a ogni apertura di pagina) e un documento senza
        // questo campo non verrebbe restituito da nessuna di quelle query.
        statoCommessa: "attiva",
        color: form.color,
        description: form.description,
        parentId: senzaCliente ? null : parentId, clienteId, clienteNome: cNome,
        senzaCliente: !!senzaCliente,
        isParent: false,
        responsabileUid,
        responsabileNome: utenti.find(u=>u.uid===responsabileUid)?.displayName || "",
        membri: membri.length>0 ? membri : [],
        hasMembri: membri.length>0,
        // Piano di carico
        dataInizio: form.dataInizio||"",
        dataFine: form.dataFine||"",
        oreGiornaliere: parseFloat(form.oreGiornaliere)||8,
        squadra,
        operaiNecessari: squadraTotale(squadra),
        livelloMin: squadra.length>0?Math.max(...squadra.map(r=>r.livello||0)):0,
        tipoEsecuzione,
        impreseEsterne: impreseDaSalvare(tipoEsecuzione, impreseEsterne),
        sede: sedeLavori || null,
        cantiere: sicurezzaDisponibile && eCantiere===true,
        organizationId: effectiveOrgId,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        archived: false,
        manutenzioneAbilitata: form.manutenzioneCommessa||false,
        manutenzioneDiProjectId: form.manutenzioneCommessa ? (form.manutenzioneDiId||null) : null,
      };
      const ref = await addDoc(collection(db,"projects"), data);
      // Sicurezza di cantiere: scheda creata subito, il responsabile riceve notifica ed email (dal server)
      if (sicurezzaDisponibile && eCantiere===true) {
        try {
          await attivaSicurezzaCantiere({ projectId: ref.id, project: data, organizationId: effectiveOrgId,
            responsabile: { uid: respSicurezza, nome: utenti.find(u=>u.uid===respSicurezza)?.displayName || "" }, utente: currentUser });
        } catch (errSic) { console.warn("Sicurezza di cantiere non attivata:", errSic); addToast("Commessa creata, ma la sicurezza di cantiere non si è attivata: attivala dalla scheda 🦺 Sicurezza","error"); }
      }
      await addDoc(collection(db,"sections"),{name:"In corso",projectId:ref.id,organizationId:effectiveOrgId,createdAt:serverTimestamp()});
      await addDoc(collection(db,"sections"),{name:"Completate",projectId:ref.id,organizationId:effectiveOrgId,createdAt:serverTimestamp()});
      // Contratto di Manutenzione creato nella stessa schermata, non in una seconda visita
      // separata a "Contratti di Manutenzione" — stessa identica struttura di documento
      // usata là, per non tenere due copie della stessa forma che rischiano di divergere.
      if (form.manutenzioneCommessa) {
        await addDoc(collection(db,"contrattiManutenzione"), {
          orgId: effectiveOrgId,
          projectId: ref.id, projectName: form.name.trim(), codiceCommessa: form.codiceCommessa||"",
          clienteId: clienteId||"", clienteNome: cNome||"",
          responsabileUid: responsabileUid||"", responsabileNome: utenti.find(u=>u.uid===responsabileUid)?.displayName||"",
          dataInizio: form.contrDataInizio||"", dataFine: form.contrDataFine||"", mesiPreavviso: 6, dataDisdetta: "", dataDisdettaManuale: false,
          tempoInterventoFeriale: "", tempoInterventoFestivo: "", tempoInterventoNotturno: "",
          costoFeriale: parseFloat(form.costoFeriale)||0, costoFestivo: parseFloat(form.costoFestivo)||0, costoNotturno: parseFloat(form.costoNotturno)||0,
          destinazioneRicavi: "stessaCommessa", commessaRicaviId: "", commessaRicaviNome: "",
          promemoria: true, promemoriaGiorni: 60, note: "",
          creatoDaUid: currentUser?.uid, creatoDaNome: currentUser?.displayName||"",
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      }
      logEvento({ orgId:effectiveOrgId, tipo:"COMMESSA_CREATA", ...TIPI_EVENTO.COMMESSA_CREATA,
        titolo:`Nuova ${senzaCliente?"area di lavoro":"commessa"} creata: ${form.name.trim()}`,
        descrizione: senzaCliente ? "Area di lavoro personale, senza cliente" : `Cliente: ${cNome}`,
        uid:currentUser.uid, nomeUtente:currentUser.displayName||"",
        refId:ref.id, refTipo:"project", refNome:form.name.trim(),
        clienteId, clienteNome:cNome, projectId:ref.id, projectName:form.name.trim(),
        navigateTo:{page:"project",projectId:ref.id}
      });
      addToast("Commessa creata ✅","success");
      if(onCreated) onCreated({ id:ref.id, ...data });
      onClose();
    } catch(err){ addToast(messaggioErrore(err, "creare la commessa"),"error"); }
    setLoading(false);
  };

  const inp = {width:"100%",padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"};

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:480}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:8}}><Plus size={15} color="#7c3aed"/>{senzaCliente?"Nuova area di lavoro":"Nuova commessa"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        {senzaCliente ? (
          <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:12,color:"#92400e",fontWeight:600}}>
            📋 Area di lavoro personale — ricorda: pensata per il solo uso interno, non entra nelle statistiche aziendali
          </div>
        ) : (
          <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:12,color:"#7c3aed",fontWeight:600}}>
            Cliente: {clienteNome}
          </div>
        )}
        <form onSubmit={e=>{e.preventDefault();}}>
          {prefill && (
            <div style={{background:"#ede9fe",border:"1px solid #c4b5fd",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:11,color:"#4c1d95"}}>
              ✅ <strong>Dati pre-compilati dalla simulazione.</strong> Verifica e completa i campi mancanti prima di salvare.
            </div>
          )}
          {/* Codice commessa — obbligatorio, formato imposto */}
          <div className="form-group">
            <label className="form-label">Codice commessa * <span style={{fontSize:11,color:"#9ca3af",fontWeight:400}}>(formato: numero-anno)</span></label>
            <div style={{fontSize:11,color:"#9ca3af",marginBottom:6}}>
              Identifica univocamente la commessa. Usato per la numerazione dei rapportini.<br/>
              Esempi: <strong>56-26</strong> · <strong>1000-26</strong> · <strong>11-26</strong>
            </div>
            {codiceSuggerito && form.codiceCommessa===codiceSuggerito && (
              <div style={{fontSize:11,color:"#7c3aed",marginBottom:4,fontWeight:600}}>
                💡 Proposto in automatico in base allo storico di tutte le commesse dell'organizzazione — modificalo pure se preferisci un altro codice.
              </div>
            )}
            {!codiceSuggerito && !orgSettings.formatoNumerazioneCommesse && (
              <div style={{fontSize:11,color:"#9ca3af",marginBottom:4}}>
                ℹ️ Numerazione automatica non attiva — vai in Impostazioni Organizzazione → Configurazione economica per impostarla.
                {isAdmin && onNavigate && (
                  <button type="button" onClick={()=>{onClose();onNavigate({page:"impostazioni-org"});}}
                    style={{marginLeft:8,padding:"2px 10px",background:"none",border:"1px solid #7c3aed",borderRadius:6,fontSize:11,fontWeight:700,color:"#7c3aed",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Vai a Impostazioni →
                  </button>
                )}
              </div>
            )}
            {!codiceSuggerito && orgSettings.formatoNumerazioneCommesse && tutteCommesseOrg.length===0 && (
              <div style={{fontSize:11,color:"#9ca3af",marginBottom:4}}>
                ℹ️ Prima commessa dell'organizzazione: nessun numero precedente da cui proporne uno.
              </div>
            )}
            {!codiceSuggerito && orgSettings.formatoNumerazioneCommesse && tutteCommesseOrg.length>0 && (
              <div style={{fontSize:11,color:"#d97706",marginBottom:4}}>
                ⚠️ Non riesco a proporre un numero: nessuna commessa esistente nell'organizzazione ha un codice nel formato "numero-numero" (es. "12-26").
              </div>
            )}
            <input className="input" value={form.codiceCommessa}
              onChange={e=>{
                // Permette solo cifre e trattino
                const val = e.target.value.replace(/[^0-9-]/g,"");
                setForm(f=>({...f,codiceCommessa:val}));
              }}
              placeholder="Es. 56-26" required
              style={{fontFamily:"monospace",fontWeight:700,fontSize:15,letterSpacing:"0.05em"}}/>
            {form.codiceCommessa && !/^\d+-\d+$/.test(form.codiceCommessa) && (
              <div style={{fontSize:11,color:"#dc2626",marginTop:4}}>
                ⚠️ Formato non valido — usa: numero-anno (es. 56-26)
              </div>
            )}
            {form.codiceCommessa && /^\d+-\d+$/.test(form.codiceCommessa) && (
              <div style={{fontSize:11,color:"#059669",marginTop:4}}>
                ✓ I rapportini saranno numerati: {new Date().getFullYear()}_{form.codiceCommessa}_01, _02, ...
              </div>
            )}
          </div>

          {/* N. ordine dell'azienda (W440) — facoltativo, testo libero */}
          <div className="form-group">
            <label className="form-label" htmlFor="np-numero-ordine">N. ordine <span style={{fontSize:11,color:"#9ca3af",fontWeight:400}}>(facoltativo)</span></label>
            <div style={{fontSize:11,color:"#9ca3af",marginBottom:4}}>Il numero d'ordine interno dell'azienda (es. quello del gestionale): compare in prima colonna in Le mie commesse, Richiesta di fatturazione e Registro Commesse. Si può aggiungere anche dopo, dalla testata della commessa.</div>
            <input id="np-numero-ordine" className="input" value={form.numeroOrdine} maxLength={40} onChange={e=>setForm(f=>({...f,numeroOrdine:e.target.value}))} placeholder="Es. 4500123"/>
          </div>

          {/* Nome commessa — libero */}
          <div className="form-group">
            <label className="form-label">Nome / descrizione commessa *</label>
            <div style={{fontSize:11,color:"#9ca3af",marginBottom:4}}>Descrizione libera — es. "Manutenzione cabina", "SHE", "Impianto fotovoltaico"</div>
            <input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Es. Manutenzione straordinaria" required/>
          </div>
          <div className="form-group">
            <label className="form-label">Descrizione (opzionale)</label>
            <input style={inp} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Descrizione breve..."/>
          </div>

          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"10px 0",borderTop:"1px solid #f3f4f6"}}>
            <input type="checkbox" checked={form.manutenzioneCommessa||false}
              onChange={e=>setForm(f=>({...f,manutenzioneCommessa:e.target.checked}))}
              style={{accentColor:"#7c3aed",width:16,height:16}}/>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"#374151"}}>🔧 È una commessa di manutenzione</div>
              <div style={{fontSize:11,color:"#9ca3af"}}>Attiva subito il modulo Gestione Manutenzione, senza doverlo accendere manualmente dopo</div>
            </div>
          </label>

          {form.manutenzioneCommessa && (
            <div style={{padding:"0 0 12px 26px"}}>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>
                È manutenzione di quale commessa già esistente? (facoltativo)
              </label>
              <select className="input" value={form.manutenzioneDiId||""}
                onChange={e=>{
                  const sel = commesseCliente.find(c=>c.id===e.target.value);
                  setForm(f=>({
                    ...f,
                    manutenzioneDiId: e.target.value,
                    codiceCommessa: (!f.codiceCommessa.trim() && sel?.codiceCommessa) ? `${sel.codiceCommessa}-M` : f.codiceCommessa,
                  }));
                }}>
                <option value="">— Nessuna, è indipendente —</option>
                {commesseCliente.map(c=><option key={c.id} value={c.id}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.name}</option>)}
              </select>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:4}}>
                Se selezioni una commessa, il codice qui sotto si propone in automatico (es. "80-26-M") — puoi comunque modificarlo liberamente.
              </div>

              {/* Contratto compilabile subito, nella stessa schermata — invece di dover
                  ricordarsi una seconda visita separata a "Contratti di Manutenzione" per
                  inserire tariffe e scadenze. Tutto facoltativo qui: si può sempre
                  completare o correggere dopo, dalla pagina dedicata. */}
              <div style={{marginTop:16,paddingTop:14,borderTop:"1px dashed #e5e7eb"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:10}}>Contratto — puoi compilarlo subito, o farlo dopo da "Contratti di Manutenzione"</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div>
                    <label style={{fontSize:10,fontWeight:600,color:"#9ca3af",display:"block",marginBottom:3}}>Data inizio contratto</label>
                    <input className="input" type="date" value={form.contrDataInizio||""} onChange={e=>setForm(f=>({...f,contrDataInizio:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,fontWeight:600,color:"#9ca3af",display:"block",marginBottom:3}}>Data scadenza</label>
                    <input className="input" type="date" value={form.contrDataFine||""} onChange={e=>setForm(f=>({...f,contrDataFine:e.target.value}))}/>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:4}}>
                  {[["Feriale","costoFeriale"],["Festivo","costoFestivo"],["Notturno","costoNotturno"]].map(([lbl,key])=>(
                    <div key={key}>
                      <label style={{fontSize:10,fontWeight:600,color:"#9ca3af",display:"block",marginBottom:3}}>Costo {lbl} (€/intervento)</label>
                      <input className="input" type="number" min="0" step="0.01" value={form[key]||""} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}/>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Responsabile commessa — obbligatorio solo se esiste già qualcuno tra cui scegliere */}
        <div className="form-group" style={{marginBottom:16}}>
          <label className="form-label" style={{display:"flex",alignItems:"center",gap:6}}>
            Responsabile della commessa{utenti.length>0 ? " *" : ""}
            {utenti.length>0 && <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:"#fee2e2",color:"#dc2626",fontWeight:700}}>OBBLIGATORIO</span>}
          </label>
          <select value={responsabileUid} onChange={e=>setResponsabileUid(e.target.value)}
            style={{...inp, borderColor: (responsabileUid || utenti.length===0) ? "#7c3aed" : "#fca5a5",
              borderWidth: (responsabileUid || utenti.length===0) ? 1 : 2,
              background: (responsabileUid || utenti.length===0) ? "#fff" : "#fef2f2"}}>
            <option value="">— Seleziona il responsabile —</option>
            {utenti.map(u=>(
              <option key={u.uid} value={u.uid}>{u.displayName}</option>
            ))}
          </select>
          {!responsabileUid && utenti.length>0 && (
            <div style={{fontSize:11,color:"#dc2626",marginTop:4}}>
              ⚠️ La commessa non può essere creata senza un Responsabile assegnato.
            </div>
          )}
          {utenti.length===0 && (
            <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>
              Nessuna persona ancora registrata in questa organizzazione — puoi assegnare un responsabile in seguito, appena qualcuno si unisce.
            </div>
          )}
          {responsabileUid && (
            <div style={{fontSize:11,color:"#059669",marginTop:4}}>
              ✓ Responsabile: <strong>{utenti.find(u=>u.uid===responsabileUid)?.displayName}</strong>
            </div>
          )}
        </div>

        {/* Sede dei lavori */}
        {!senzaCliente && (
          <div style={{marginBottom:12}}>
            <SelettoreSede clienteId={clienteIdSede} value={sedeLavori} onChange={setSedeLavori} proposta={propostaSede} etichetta="📍 Sede dei lavori"/>
            <div style={{fontSize:10.5,color:"#9ca3af",marginTop:3}}>Si propone in rapportini, sopralluoghi, DDT, giornale e sicurezza. Le sedi si gestiscono nell'anagrafica del cliente.</div>
          </div>
        )}

        {/* Piano di carico */}
          <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:8,padding:"12px 14px",marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:"#0369a1",marginBottom:10}}>📊 Piano di carico — per il Simulatore Disponibilità Maestranze</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Data inizio prevista</label>
                <input className="input" type="date" value={form.dataInizio} onChange={e=>setForm(f=>({...f,dataInizio:e.target.value}))}/>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Data fine prevista</label>
                <input className="input" type="date" value={form.dataFine} onChange={e=>setForm(f=>({...f,dataFine:e.target.value}))}/>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:6}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Ore giornaliere/operaio</label>
                <select className="input" value={form.oreGiornaliere} onChange={e=>setForm(f=>({...f,oreGiornaliere:e.target.value}))}>
                  {[4,5,6,7,8,9,10].map(h=><option key={h} value={h}>{h}h</option>)}
                </select>
              </div>
            </div>
            {/* Chi esegue i lavori */}
            <div className="form-group" style={{margin:"10px 0 0"}}>
              <label className="form-label">Chi esegue i lavori</label>
              <EsecuzioneCommessaEditor tipo={tipoEsecuzione} onTipo={cambiaTipoEsecuzione} imprese={impreseEsterne} onImprese={setImpreseEsterne}/>
            </div>
            {/* Composizione squadra (o presidio interno, in subappalto) */}
            <div className="form-group" style={{margin:"10px 0 0"}}>
              <label className="form-label">{etSquadra.titolo}</label>
              <div style={{fontSize:11,color:"#9ca3af",marginBottom:6}}>{etSquadra.aiuto || "Indica quante figure servono e con quale livello professionalità"}</div>
              <SquadraEditor squadra={squadra} onChange={setSquadra}/>
            </div>
            <div style={{display:"none"}}> {/* placeholder per chiudere grid */}
            </div>
            {(!form.dataInizio||!form.dataFine||squadra.length===0) && (
              <div style={{fontSize:10,color:"#0369a1",display:"flex",alignItems:"center",gap:4}}>
                ⚠️ Compila tutti i campi per aggiornare il simulatore disponibilità
              </div>
            )}
          </div>

          {/* Sicurezza di cantiere */}
          {sicurezzaDisponibile && !senzaCliente && (() => {
            const gg = giorniLavorativi(form.dataInizio, form.dataFine);
            const suggerito = (gg!==null && gg>=GIORNI_SUGGERIMENTO_CANTIERE) || tipoEsecuzione!=="interno";
            return (
              <div style={{background:"#fef2f2",border:`1px solid ${eCantiere===null?"#fca5a5":"#fecaca"}`,borderRadius:8,padding:"12px 14px",marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:"#b91c1c",marginBottom:6}}>🦺 È un cantiere? *</div>
                <div style={{fontSize:11,color:"#7f1d1d",marginBottom:8}}>Cantiere temporaneo o mobile (Titolo IV del D.Lgs. 81/08): Nexiora prepara la checklist degli adempimenti di sicurezza e avvisa l'addetto alla sicurezza.
                  {suggerito && eCantiere===null && <b> Per durata o imprese coinvolte, probabilmente sì.</b>}</div>
                <div style={{display:"flex",gap:8,marginBottom:eCantiere?8:0}}>
                  {[[true,"Sì, è un cantiere"],[false,"No"]].map(([v,l])=>(
                    <button key={l} type="button" onClick={()=>{ setECantiere(v); if(v && !respSicurezza && orgSettings.responsabileSicurezzaUid) setRespSicurezza(orgSettings.responsabileSicurezzaUid); }}
                      style={{flex:1,padding:"7px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${eCantiere===v?"#b91c1c":"#e5e7eb"}`,background:eCantiere===v?"#fff":"#fff",color:eCantiere===v?"#b91c1c":"#6b7280",boxShadow:eCantiere===v?"0 0 0 1px #b91c1c inset":"none"}}>{l}</button>
                  ))}
                </div>
                {eCantiere && (
                  <select value={respSicurezza} onChange={e=>setRespSicurezza(e.target.value)} style={{...inp,borderColor:respSicurezza?"#e5e7eb":"#fca5a5"}}>
                    <option value="">— Addetto alla sicurezza e alla documentazione di cantiere —</option>
                    {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                  </select>
                )}
              </div>
            );
          })()}

          <div className="form-group">
            <label className="form-label">Colore</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {COLORS.map(c=>(
                <div key={c} onClick={()=>setForm(f=>({...f,color:c}))}
                  style={{width:26,height:26,borderRadius:"50%",background:c,cursor:"pointer",border:form.color===c?"3px solid #111827":"3px solid transparent",transition:"border .15s"}}/>
              ))}
            </div>
          </div>
          {/* Selezione membri */}
          <div className="form-group">
            <label className="form-label">Membri del progetto</label>
            <div style={{fontSize:11,color:"#9ca3af",marginBottom:8}}>Chi non viene aggiunto non vedrà questa commessa.</div>
            <div style={{border:"1px solid #e5e7eb",borderRadius:8,overflow:"hidden",maxHeight:200,overflowY:"auto"}}>
              {utenti.map(u=>{
                const membro = membri.find(m=>m.uid===u.uid);
                return (
                  <div key={u.uid} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderBottom:"1px solid #f3f4f6",background:membro?"#faf5ff":"#fff"}}>
                    <input type="checkbox" checked={!!membro} onChange={()=>toggleMembro(u)}
                      style={{accentColor:"#7c3aed",width:15,height:15,flexShrink:0}}/>
                    <span style={{flex:1,fontSize:13,fontWeight:membro?600:400,color:membro?"#374151":"#6b7280"}}>{u.displayName}</span>
                    {membro && (
                      <select value={membro.ruolo} onChange={e=>cambiaRuolo(u.uid,e.target.value)}
                        style={{fontSize:11,padding:"2px 6px",border:"1px solid #e9d5ff",borderRadius:6,background:"#ede9fe",color:"#7c3aed",fontWeight:600,fontFamily:"'Inter',sans-serif",outline:"none"}}>
                        <option value="responsabile">Responsabile</option>
                        <option value="operativo">Operativo</option>
                        <option value="osservatore">Osservatore</option>
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
            {membri.length===0 && <div style={{fontSize:11,color:"#f59e0b",marginTop:4}}>⚠️ Nessun membro — il progetto sarà visibile a tutti.</div>}
          </div>

          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="button" onClick={submit} className="btn btn-primary" disabled={loading}>{loading?"Creazione...":"Crea commessa"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal scelta iniziale: è destinata a un cliente? ─────────────────────────
function ModalScegliTipo({ onClose, onCreated, onNavigate, prefill }) {
  const [scelta, setScelta] = useState(null); // null | "si" | "no"

  if (scelta === "si") return <ModalSelezionaCliente onClose={onClose} onCreated={onCreated} onNavigate={onNavigate} prefill={prefill}/>;
  if (scelta === "no") return <ModalNuovaCommessa senzaCliente={true} onClose={onClose} onCreated={onCreated} onNavigate={onNavigate} prefill={prefill}/>;

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:420}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:8}}><Plus size={15} color="#7c3aed"/>Nuova commessa</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <p style={{fontSize:14,fontWeight:600,color:"#374151",marginBottom:16}}>Questa commessa è destinata a un Cliente?</p>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <button onClick={()=>setScelta("si")} className="btn btn-primary" style={{padding:"12px",fontSize:14}}>
            <Building2 size={15} style={{marginRight:6,verticalAlign:"middle"}}/> Sì, è per un cliente
          </button>
          <button onClick={()=>setScelta("no")} className="btn btn-secondary" style={{padding:"12px",fontSize:14}}>
            📋 No, è un'area di lavoro personale/interna
          </button>
        </div>
        <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"10px 12px",marginTop:14}}>
          <p style={{fontSize:11.5,color:"#92400e",fontWeight:600,marginBottom:4}}>
            💡 Consiglio: scegli "Sì" quasi sempre
          </p>
          <p style={{fontSize:11,color:"#92400e",lineHeight:1.5}}>
            Anche per un cliente piccolo od occasionale conviene creare un'anagrafica (bastano pochi secondi) — è l'unico modo per avere richieste commerciali, DDT e statistiche corrette. Riserva "No" solo a lavori davvero interni (es. manutenzione dei vostri stessi uffici, prove, formazione) — e tienine il numero contenuto: più ne crei, meno la Sidebar resterà ordinata, dato che non si raggruppano tra loro.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Export principale: decide quale modal mostrare ───────────────────────────
export default function NewProjectModal({ onClose, onCreated, parentId, clienteNome, prefill, onNavigate }) {
  if(parentId){
    return <ModalNuovaCommessa parentId={parentId} clienteNome={clienteNome} prefill={prefill} onClose={onClose} onCreated={onCreated} onNavigate={onNavigate}/>;
  }
  return <ModalScegliTipo onClose={onClose} onCreated={onCreated} onNavigate={onNavigate}/>;
}
